
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../store';
import { UserRole } from '../types';
import { Zap, Shield, Star, Hammer, ChevronLeft, Ruler, ArrowRight } from 'lucide-react';

const Onboarding: React.FC = () => {
  const { setCurrentUser } = useApp();
  const navigate = useNavigate();

  const handleSelectRole = (role: UserRole) => {
    const user = {
      id: role === UserRole.REQUESTER ? 'user123' : 'artisan456',
      name: role === UserRole.REQUESTER ? 'Alex Requester' : 'Sam Artisan',
      role: role,
      city: 'San Francisco'
    };
    setCurrentUser(user);
    navigate(role === UserRole.REQUESTER ? '/requester' : '/artisan');
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 max-w-4xl mx-auto py-20 reveal">
      {/* Header */}
      <div className="text-center space-y-8 mb-20">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-stone-900 text-amber-500 rounded-2xl shadow-2xl group cursor-pointer hover:rotate-12 transition-transform">
          <Hammer size={32} strokeWidth={2.5} />
        </div>
        <h1 className="text-7xl font-black text-stone-900 tracking-tight font-serif italic">
          Choose Your Path.
        </h1>
        <p className="text-xl text-stone-400 font-medium max-w-md mx-auto">
          The Handlo workshop supports both the seekers of craft and the masters of it.
        </p>
      </div>

      {/* Grid */}
      <div className="grid md:grid-cols-2 gap-8 w-full">
        <button
          onClick={() => handleSelectRole(UserRole.REQUESTER)}
          className="premium-card p-12 rounded-[3rem] text-left group flex flex-col justify-between min-h-[320px]"
        >
          <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-700 mb-10 group-hover:bg-amber-500 group-hover:text-white transition-all">
            <Zap size={32} />
          </div>
          <div>
            <h3 className="text-4xl font-black text-stone-900 font-serif italic mb-3">Seeker</h3>
            <p className="text-stone-400 font-medium leading-relaxed mb-8">I have a project that requires a master's hand and local expertise.</p>
            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-amber-700 group-hover:gap-5 transition-all">
              Launch Commission <ArrowRight size={14} />
            </div>
          </div>
        </button>

        <button
          onClick={() => handleSelectRole(UserRole.ARTISAN)}
          className="bg-stone-900 p-12 rounded-[3rem] text-left group flex flex-col justify-between min-h-[320px] transition-all hover:translate-y-[-4px] hover:shadow-2xl"
        >
          <div className="w-16 h-16 bg-stone-800 rounded-2xl flex items-center justify-center text-amber-500 mb-10 group-hover:bg-amber-500 group-hover:text-stone-900 transition-all">
            <Hammer size={32} />
          </div>
          <div>
            <h3 className="text-4xl font-black text-white font-serif italic mb-3">Master</h3>
            <p className="text-stone-500 font-medium leading-relaxed mb-8">I am a vetted artisan looking to apply my craft to local commissions.</p>
            <div className="flex items-center gap-3 text-[10px] font-black uppercase tracking-widest text-amber-500 group-hover:gap-5 transition-all">
              Enter the Feed <ArrowRight size={14} />
            </div>
          </div>
        </button>
      </div>

      {/* Trust */}
      <div className="mt-20 flex gap-12 text-[10px] font-black uppercase tracking-[0.4em] text-stone-300">
        <div className="flex items-center gap-2"><Shield size={16} /> Verified</div>
        <div className="flex items-center gap-2"><Star size={16} /> Elite Tier</div>
        <div className="flex items-center gap-2"><Ruler size={16} /> Precision</div>
      </div>
    </div>
  );
};

export default Onboarding;
